/**
 * Fitness-Fatigue Model (Banister Model)
 * 
 * Performance = Fitness - Fatigue
 * 
 * - Fitness: Slow-building, slow-decaying (15-20 day half-life)
 * - Fatigue: Fast-building, fast-decaying (3-5 day half-life)
 * 
 * Used for:
 * - Predicting performance readiness
 * - Optimal deload timing
 * - Supercompensation windows
 */

import type { SessionLog } from './sessionStorage';
import type { FitnessModel } from './types/userProfile';
import { isPerHandLoad } from './exerciseDatabase';

/**
 * Calculate session training load
 * Load = Volume × Intensity × RPE factor
 */
export function calculateSessionLoad(session: SessionLog): number {
  if (session.sessionType !== 'pt' || !('exercises' in session)) {
    return 0;
  }
  
  let totalLoad = 0;
  
  session.exercises.forEach(exercise => {
    if (exercise.sets) {
      exercise.sets.forEach(set => {
        // Inline tonnage (per-hand/unilateral aware) — matches computeSetVolume
        // semantics. Inlined to avoid a circular import
        // (volumeTracking → sessionStorage → fitnessFatigueModel).
        const s = set as any;
        const reps = s.reps || 0;
        const perHandMult = s.unilateral
          ? 1
          : ((typeof s.perHand === 'boolean' ? s.perHand : isPerHandLoad(exercise.exerciseName)) ? 2 : 1);
        const totalReps = s.unilateral
          ? reps + (typeof s.rightReps === 'number' ? s.rightReps : reps)
          : reps;
        const volume = (s.weight || 0) * perHandMult * totalReps;
        const rpeFactor = (set.rpe || 7) / 10; // Normalize RPE to 0-1
        totalLoad += volume * rpeFactor;
      });
    }
  });
  
  // Normalize to 0-100 scale (divide by typical session load)
  // Typical session: 10,000 lbs total volume
  return Math.min(100, (totalLoad / 10000) * 100);
}

/**
 * Update fitness-fatigue model with new session
 */
export function updateFitnessFatigue(
  currentModel: FitnessModel,
  session: SessionLog
): FitnessModel {
  const sessionLoad = calculateSessionLoad(session);
  const sessionDate = new Date(session.date);
  const lastCalculated = new Date(currentModel.lastCalculated);
  
  // Calculate days since last update
  const daysSinceLastUpdate = Math.max(0, 
    (sessionDate.getTime() - lastCalculated.getTime()) / (24 * 60 * 60 * 1000)
  );
  
  // Apply decay to existing fitness and fatigue
  const fitnessDecay = Math.pow(0.5, daysSinceLastUpdate / currentModel.fitnessDecayRate);
  const fatigueDecay = Math.pow(0.5, daysSinceLastUpdate / currentModel.fatigueDecayRate);
  
  let newFitness = currentModel.currentFitness * fitnessDecay;
  let newFatigue = currentModel.currentFatigue * fatigueDecay;
  
  // Add new training stimulus
  // 30% of load becomes fitness, 70% becomes fatigue
  const fitnessGain = sessionLoad * 0.3;
  const fatigueGain = sessionLoad * 0.7;
  
  newFitness = Math.min(100, newFitness + fitnessGain);
  newFatigue = Math.min(100, newFatigue + fatigueGain);
  
  // Calculate readiness (can be negative)
  const readiness = newFitness - newFatigue;
  
  // Predict deload need
  const predictedDeloadWeek = predictDeloadTiming(newFitness, newFatigue, sessionDate);
  
  return {
    ...currentModel,
    currentFitness: Math.round(newFitness),
    currentFatigue: Math.round(newFatigue),
    readiness: Math.round(readiness),
    predictedDeloadWeek,
    lastCalculated: sessionDate,
  };
}

/**
 * Predict when deload will be needed
 * Based on fatigue accumulation trajectory
 */
function predictDeloadTiming(
  fitness: number,
  fatigue: number,
  currentDate: Date
): number | undefined {
  // If fatigue is already high, deload soon
  if (fatigue > 70) {
    return getCurrentWeek(currentDate) + 1;
  }
  
  // If fatigue is moderate and fitness is high, can continue
  if (fatigue < 50 && fitness > 70) {
    return undefined; // No immediate deload needed
  }
  
  // If readiness is declining (fatigue approaching fitness)
  const readiness = fitness - fatigue;
  if (readiness < 20) {
    return getCurrentWeek(currentDate) + 1;
  }
  
  return undefined;
}

/**
 * Get current week number (simplified - assumes week starts Monday)
 */
function getCurrentWeek(date: Date): number {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  return Math.ceil((days + startOfYear.getDay() + 1) / 7);
}

/**
 * Predict performance trajectory for next N days
 */
export function predictPerformanceTrajectory(
  currentModel: FitnessModel,
  daysAhead: number,
  plannedSessionLoads: number[] = []
): Array<{
  day: number;
  fitness: number;
  fatigue: number;
  readiness: number;
  recommendation: 'train' | 'light' | 'rest' | 'deload';
}> {
  const trajectory: ReturnType<typeof predictPerformanceTrajectory> = [];
  
  let fitness = currentModel.currentFitness;
  let fatigue = currentModel.currentFatigue;
  
  for (let day = 1; day <= daysAhead; day++) {
    // Apply daily decay
    const fitnessDecay = Math.pow(0.5, 1 / currentModel.fitnessDecayRate);
    const fatigueDecay = Math.pow(0.5, 1 / currentModel.fatigueDecayRate);
    
    fitness *= fitnessDecay;
    fatigue *= fatigueDecay;
    
    // Apply planned session load if provided
    if (plannedSessionLoads[day - 1]) {
      const load = plannedSessionLoads[day - 1];
      fitness += load * 0.3;
      fatigue += load * 0.7;
    }
    
    const readiness = fitness - fatigue;
    
    // Determine recommendation
    let recommendation: 'train' | 'light' | 'rest' | 'deload' = 'train';
    if (fatigue > 70) {
      recommendation = 'deload';
    } else if (readiness < 10) {
      recommendation = 'rest';
    } else if (readiness < 30) {
      recommendation = 'light';
    }
    
    trajectory.push({
      day,
      fitness: Math.round(fitness),
      fatigue: Math.round(fatigue),
      readiness: Math.round(readiness),
      recommendation,
    });
  }
  
  return trajectory;
}

/**
 * Calculate optimal deload timing
 * Returns number of days until deload should occur
 */
export function calculateOptimalDeloadTiming(
  currentModel: FitnessModel,
  averageSessionLoad: number
): {
  daysUntilDeload: number;
  confidence: 'low' | 'medium' | 'high';
  reasoning: string;
} {
  // Simulate future with typical training load
  const trajectory = predictPerformanceTrajectory(
    currentModel,
    21, // 3 weeks ahead
    Array(21).fill(averageSessionLoad)
  );
  
  // Find when deload is recommended
  const deloadDay = trajectory.findIndex(t => t.recommendation === 'deload' || t.recommendation === 'rest');
  
  if (deloadDay === -1) {
    return {
      daysUntilDeload: 21,
      confidence: 'low',
      reasoning: 'No deload needed in next 3 weeks based on current trajectory',
    };
  }
  
  if (deloadDay <= 7) {
    return {
      daysUntilDeload: deloadDay,
      confidence: 'high',
      reasoning: `Fatigue accumulation high. Deload recommended within ${deloadDay} days.`,
    };
  }
  
  return {
    daysUntilDeload: deloadDay,
    confidence: 'medium',
    reasoning: `Current trajectory suggests deload in ${deloadDay} days.`,
  };
}

/**
 * Check if user is in supercompensation window
 * (fitness high, fatigue low)
 */
export function isInSupercompensation(model: FitnessModel): {
  inWindow: boolean;
  readiness: number;
  recommendation: string;
} {
  const { currentFitness, currentFatigue, readiness } = model;
  
  // Supercompensation: High fitness, low fatigue
  const inWindow = currentFitness > 70 && currentFatigue < 30 && readiness > 40;
  
  let recommendation = '';
  if (inWindow) {
    recommendation = 'Excellent time to test PRs or push intensity!';
  } else if (currentFatigue > 60) {
    recommendation = 'High fatigue. Consider rest or deload.';
  } else if (readiness < 20) {
    recommendation = 'Low readiness. Light training or rest recommended.';
  } else {
    recommendation = 'Continue normal training.';
  }
  
  return {
    inWindow,
    readiness,
    recommendation,
  };
}




